import { consoleHandlerError, extractErrorMessage } from "@/utils/message";
import { createTakeLatest } from "@/utils/takeLatest";
import { resolveDateRange, buildHeadersByPeriod, formatPeriodLabel } from "./utils/dateRange";

// Factory de los módulos Vuex de los tabs de balance por nivel (Activos, Pasivos,
// Patrimonio). Los tres consumen el MISMO contrato del backend
// (`build_balance_breakdown`): report_data con SALDOS acumulados por período,
// jerarquía L2/L3, sales_by_period (para el ratio) y accumulated_data (saldo al
// cierre de la ventana). Sólo difieren en el endpoint de la API y en las
// etiquetas del total / ratio, así que se parametrizan acá en vez de triplicar el
// módulo. `fetchBreakdown(initDate, endDate, groupBy, period)` y
// `fetchSummary(endDate)` son las funciones de `@/api/financialReport`.
export function createBalanceLevelReport({
    fetchBreakdown,
    fetchSummary,
    totalLabel,
    ratioLabel,
    errorLabel,
    summaryErrorLabel,
    logPrefix,
}) {
    // Take-latest por action (mismo patrón que classification.js): sólo el
    // fetch más reciente commitea — una respuesta stale que llega tarde no
    // clobbea estado fresco (caso típico: dos cambios de filtro rápidos — la
    // primera request, más lenta, llegaría después de la segunda) y su error
    // no pisa el estado del fetch vigente. Viven en el closure del factory →
    // un par por módulo (assets/liabilities/equity no se pisan entre sí).
    const runLatestData = createTakeLatest();
    const runLatestSummary = createTakeLatest();

    const state = () => ({
        selectedCounterpartTypes: [],
        monthHeaders: [],
        accountsData: {},
        level2Accounts: [],
        level3Accounts: [],
        hierarchy: {},
        salesByPeriod: {},
        /**
         * Saldo al cierre de la ventana + comparación contra el cierre de la
         * ventana previa de igual largo. Indexado por L2/L3 + "__total__".
         * `pct` es null cuando prevAcc === 0 (frontend muestra "—").
         * @type {Object.<string, { acc: number, prevAcc: number, pct: number | null }>}
         */
        accumulatedData: {},
        periodRange: { init_date: null, end_date: null },
        isLoading: false,
        error: null,
        summaryCards: null,
        isSummaryLoading: false,
        summaryError: null,
    });

    const EMPTY_ACC = { acc: 0, prevAcc: 0, pct: null };

    const getters = {
        isCuentasMode: (state) => state.selectedCounterpartTypes.length === 0,

        groupByParam: (state) => {
            if (state.selectedCounterpartTypes.length === 0) return "cuentas";
            return state.selectedCounterpartTypes.join(",");
        },

        tableRows: (state) => {
            const rows = [];
            state.level2Accounts.forEach(l2 => {
                const l2Acc = state.accumulatedData[l2] ?? EMPTY_ACC;
                rows.push({
                    label: l2,
                    level: 2,
                    isTotal: false,
                    values: state.monthHeaders.map(
                        (header) => state.accountsData[l2]?.[header] ?? 0
                    ),
                    accumulated: l2Acc.acc,
                    pct: l2Acc.pct,
                });

                const children = state.hierarchy[l2] || [];
                children.forEach(l3 => {
                    if (l3 === l2) return;
                    const l3Acc = state.accumulatedData[l3] ?? EMPTY_ACC;
                    rows.push({
                        label: l3,
                        level: 3,
                        parent: l2,
                        isTotal: false,
                        values: state.monthHeaders.map(
                            (header) => state.accountsData[l3]?.[header] ?? 0
                        ),
                        accumulated: l3Acc.acc,
                        pct: l3Acc.pct,
                    });
                });
            });
            return rows;
        },

        totalRow: (state) => {
            const values = state.monthHeaders.map((header) =>
                state.level2Accounts.reduce((sum, account) => {
                    const val = Number(state.accountsData[account]?.[header] || 0);
                    return sum + (isNaN(val) ? 0 : val);
                }, 0)
            );
            const totals = state.accumulatedData.__total__ ?? EMPTY_ACC;
            return {
                label: totalLabel,
                values,
                isTotal: true,
                accumulated: totals.acc,
                pct: totals.pct,
            };
        },

        /**
         * Ratio saldo / ventas netas por período. Numerador = total de level_2
         * del nivel de balance (saldo acumulado). Indicador de intensidad de
         * capital (para Activos), apalancamiento (Pasivos), etc.
         */
        ratioRow: (state) => {
            const values = state.monthHeaders.map((header) => {
                const total = state.level2Accounts.reduce((sum, account) => {
                    const val = Number(state.accountsData[account]?.[header] || 0);
                    return sum + (isNaN(val) ? 0 : val);
                }, 0);
                const ventas = Number(state.salesByPeriod[header] || 0);
                return ventas !== 0 ? total / ventas : 0;
            });
            return { label: ratioLabel, values, isRatio: true };
        },
    };

    const mutations = {
        SET_SELECTED_COUNTERPART_TYPES(state, types) {
            state.selectedCounterpartTypes = Array.isArray(types) ? [...types] : [];
        },
        SET_DATA(state, { level2Accounts, level3Accounts, hierarchy, accountsData, monthHeaders, periodRange, salesByPeriod, accumulatedData }) {
            state.level2Accounts = level2Accounts;
            state.level3Accounts = level3Accounts;
            state.hierarchy = hierarchy;
            state.accountsData = accountsData;
            state.monthHeaders = monthHeaders;
            state.periodRange = periodRange;
            state.salesByPeriod = salesByPeriod || {};
            state.accumulatedData = accumulatedData || {};
        },
        SET_LOADING(state, isLoading) { state.isLoading = isLoading; },
        SET_ERROR(state, error) { state.error = error; },
        SET_SUMMARY_CARDS(state, summary) { state.summaryCards = summary; },
        SET_SUMMARY_LOADING(state, isLoading) { state.isSummaryLoading = isLoading; },
        SET_SUMMARY_ERROR(state, error) { state.summaryError = error; },
    };

    const actions = {
        async fetchData({ getters, commit, rootState }) {
            commit("SET_LOADING", true);
            commit("SET_ERROR", null);

            let stale = false;
            try {
                const endMonth = rootState.reportsFilters.endMonth;
                const filterPeriod = rootState.financialReports.filterPeriod;

                // customRange/customYearRange incluidos: sin ellos, un rango
                // custom elegido por el usuario se ignoraba en estos tabs
                // mientras Resultados/Ventas sí lo honraban.
                const { initDate, endDate } = resolveDateRange(
                    endMonth,
                    rootState.reportsFilters.selectedPeriodRange,
                    filterPeriod,
                    rootState.reportsFilters.customRange,
                    rootState.reportsFilters.customYearRange,
                );

                const result = await runLatestData((signal) =>
                    fetchBreakdown(initDate, endDate, getters.groupByParam, filterPeriod, { signal }),
                );
                stale = result.stale;
                if (stale) {
                    // Error stale: se loguea (como antes) pero no pisa el
                    // estado del fetch vigente.
                    if (result.error) {
                        consoleHandlerError({ error: result.error, prefix: logPrefix, silent: true });
                    }
                    return;
                }
                const response = result.value;
                const {
                    report_data,
                    level2_accounts,
                    level3_accounts,
                    hierarchy,
                    sales_by_period,
                    accumulated_data: accumulatedRaw,
                } = response.data;

                const monthHeaders = buildHeadersByPeriod(initDate, endDate, filterPeriod);
                const accountsData = {};

                Object.keys(report_data).forEach(accountName => {
                    accountsData[accountName] = {};
                    report_data[accountName].forEach(row => {
                        const header = formatPeriodLabel(row.period, filterPeriod);
                        const amount = Number(row.total_amount);
                        accountsData[accountName][header] = isNaN(amount) ? 0 : amount;
                    });
                });

                const salesByPeriod = {};
                (sales_by_period || []).forEach(row => {
                    const header = formatPeriodLabel(row.period, filterPeriod);
                    const amount = Number(row.total_amount);
                    salesByPeriod[header] = isNaN(amount) ? 0 : amount;
                });

                // Normaliza snake→camel y castea a Number. Decimal puede llegar
                // como string desde algunos paths del selector.
                const accumulatedData = {};
                Object.entries(accumulatedRaw ?? {}).forEach(([key, value]) => {
                    accumulatedData[key] = {
                        acc: Number(value?.acc ?? 0),
                        prevAcc: Number(value?.prev_acc ?? 0),
                        pct: value?.pct == null ? null : Number(value.pct),
                    };
                });

                commit("SET_DATA", {
                    level2Accounts: level2_accounts,
                    level3Accounts: level3_accounts,
                    hierarchy,
                    accountsData,
                    monthHeaders,
                    periodRange: { init_date: initDate, end_date: endDate },
                    salesByPeriod,
                    accumulatedData,
                });
            } catch (error) {
                consoleHandlerError({ error, prefix: logPrefix, silent: true });
                commit("SET_ERROR", errorLabel);
            } finally {
                if (!stale) commit("SET_LOADING", false);
            }
        },

        async fetchSummary({ commit, rootState }) {
            commit("SET_SUMMARY_LOADING", true);
            commit("SET_SUMMARY_ERROR", null);

            let stale = false;
            try {
                // Mismo rango que la tabla: para empresas clasificador la card
                // es el ACUMULADO DEL PERÍODO seleccionado (el backend necesita
                // init_date); para empresas con mayor el backend lo ignora y
                // sigue devolviendo el saldo anual al corte (endDate).
                const { initDate, endDate } = resolveDateRange(
                    rootState.reportsFilters.endMonth,
                    rootState.reportsFilters.selectedPeriodRange,
                    rootState.financialReports.filterPeriod,
                    rootState.reportsFilters.customRange,
                    rootState.reportsFilters.customYearRange,
                );

                const result = await runLatestSummary((signal) =>
                    fetchSummary(endDate, { signal }, initDate),
                );
                stale = result.stale;
                if (stale) {
                    if (result.error) {
                        consoleHandlerError({ error: result.error, prefix: logPrefix, silent: true });
                    }
                    return;
                }
                commit("SET_SUMMARY_CARDS", Object.freeze(result.value.data));
            } catch (error) {
                consoleHandlerError({ error, prefix: logPrefix, silent: true });
                commit("SET_SUMMARY_ERROR", extractErrorMessage(error, summaryErrorLabel));
            } finally {
                if (!stale) commit("SET_SUMMARY_LOADING", false);
            }
        },
    };

    return {
        namespaced: true,
        state,
        getters,
        mutations,
        actions,
    };
}
